import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { OptimizedImage, OptimizedBackgroundImage } from "@/components/ui/optimized-image";
import {
  Camera,
  Upload,
  Heart,
  Share2,
  MapPin,
  Star,
  Trophy,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ShoppingBag,
  Calendar,
  ThumbsUp,
  Sparkles,
  Leaf,
  Sun,
  TreePine,
  Instagram,
  Twitter,
  Facebook,
  Link as LinkIcon,
  Award,
  Clock,
  Check,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Import terrarium images for placeholders
import desertTerrarium from "@assets/generated_images/desert_terrarium_product.png";
import mossTerrarium from "@assets/generated_images/moss_forest_terrarium.png";
import tropicalTerrarium from "@assets/generated_images/geometric_tropical_terrarium.png";
import heroImage from "@assets/generated_images/lush_terrarium_hero_image.png";

// Types
interface ShowcaseEntry {
  id: string;
  customerName: string;
  customerLocation: string;
  photoUrl: string;
  story: string;
  terrariumType: "tropical" | "desert" | "moss" | "forest" | "custom";
  terrariumSize: "small" | "medium" | "large" | "extra-large";
  productsUsed: string[];
  productIds?: string[];
  votes: number;
  hasVoted?: boolean;
  isFeatured: boolean;
  isSpotlight: boolean;
  createdAt: string;
  approvedAt?: string;
  status: "pending" | "approved" | "featured" | "rejected";
}

interface GalleryFilters {
  terrariumType: string;
  size: string;
  sortBy: "recent" | "popular" | "featured";
}

// Placeholder data for demo
const PLACEHOLDER_ENTRIES: ShowcaseEntry[] = [
  {
    id: "1",
    customerName: "Sarah M.",
    customerLocation: "Denver, CO",
    photoUrl: mossTerrarium,
    story: "My forest terrarium has become the centerpiece of my living room. Everyone who visits asks about it! The moss carpet is thriving and the tiny ferns have doubled in size.",
    terrariumType: "moss",
    terrariumSize: "large",
    productsUsed: ["Forest Oasis Terrarium", "Premium Moss Blend", "Miniature Ferns"],
    votes: 247,
    isFeatured: true,
    isSpotlight: false,
    createdAt: "2024-01-15T10:00:00Z",
    approvedAt: "2024-01-16T10:00:00Z",
    status: "featured",
  },
  {
    id: "2",
    customerName: "Mike T.",
    customerLocation: "Boulder, CO",
    photoUrl: desertTerrarium,
    story: "I was nervous about keeping plants alive, but my desert terrarium is so low maintenance. The succulents are doing great with minimal watering!",
    terrariumType: "desert",
    terrariumSize: "medium",
    productsUsed: ["DesertScape Terrarium", "Succulent Collection"],
    votes: 189,
    isFeatured: false,
    isSpotlight: false,
    createdAt: "2024-01-10T10:00:00Z",
    approvedAt: "2024-01-11T10:00:00Z",
    status: "approved",
  },
  {
    id: "3",
    customerName: "Emily R.",
    customerLocation: "Fort Collins, CO",
    photoUrl: tropicalTerrarium,
    story: "The tropical terrarium Ryan made for me is absolutely stunning. It sits in my home office and brings me so much joy during work hours.",
    terrariumType: "tropical",
    terrariumSize: "large",
    productsUsed: ["Tropical Paradise Terrarium", "Exotic Ferns", "Decorative Stones"],
    votes: 312,
    isFeatured: true,
    isSpotlight: true,
    createdAt: "2024-01-05T10:00:00Z",
    approvedAt: "2024-01-06T10:00:00Z",
    status: "featured",
  },
  {
    id: "4",
    customerName: "David K.",
    customerLocation: "Colorado Springs, CO",
    photoUrl: mossTerrarium,
    story: "Bought this as a gift for my wife and she absolutely loves it. The quality is incredible and it's been thriving for months.",
    terrariumType: "forest",
    terrariumSize: "medium",
    productsUsed: ["Forest Oasis Terrarium"],
    votes: 156,
    isFeatured: false,
    isSpotlight: false,
    createdAt: "2024-01-20T10:00:00Z",
    approvedAt: "2024-01-21T10:00:00Z",
    status: "approved",
  },
  {
    id: "5",
    customerName: "Jessica L.",
    customerLocation: "Lakewood, CO",
    photoUrl: desertTerrarium,
    story: "I attended Ryan's workshop and made this beauty myself! Such an amazing experience learning about terrariums while hiking in nature.",
    terrariumType: "desert",
    terrariumSize: "small",
    productsUsed: ["Workshop Experience", "Custom Desert Creation"],
    votes: 203,
    isFeatured: false,
    isSpotlight: false,
    createdAt: "2024-01-18T10:00:00Z",
    approvedAt: "2024-01-19T10:00:00Z",
    status: "approved",
  },
  {
    id: "6",
    customerName: "Chris P.",
    customerLocation: "Aurora, CO",
    photoUrl: tropicalTerrarium,
    story: "The custom design consultation was worth every penny. Ryan really listened to what I wanted and created something unique for my space.",
    terrariumType: "custom",
    terrariumSize: "extra-large",
    productsUsed: ["Custom Design Consultation", "Deluxe Custom Terrarium"],
    votes: 278,
    isFeatured: true,
    isSpotlight: false,
    createdAt: "2024-01-12T10:00:00Z",
    approvedAt: "2024-01-13T10:00:00Z",
    status: "featured",
  },
];

// Terrarium of the Month data
const TERRARIUM_OF_THE_MONTH = PLACEHOLDER_ENTRIES.find(e => e.isSpotlight) || PLACEHOLDER_ENTRIES[2];

// Header Component
function GalleryHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [, setLocation] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      role="banner"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-background/95 backdrop-blur-md border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation("/")}
            className="flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
            aria-label="Go back to home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <a
            href="/"
            className="font-serif text-2xl font-semibold tracking-tight"
            aria-label="Life Itself Studio - Go to homepage"
          >
            <span className="text-primary">Life Itself</span> Studio
          </a>
        </div>

        <nav className="flex items-center gap-4" role="navigation" aria-label="Gallery navigation">
          <button
            onClick={() => setLocation("/checkout")}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground transition-all hover:bg-primary/90"
            aria-label="Design your terrarium"
          >
            <Leaf className="w-4 h-4" aria-hidden="true" />
            <span className="hidden sm:inline">Design Yours</span>
          </button>
        </nav>
      </div>
    </header>
  );
}

// Hero Section
function GalleryHero() {
  return (
    <section className="relative h-64 md:h-80 overflow-hidden" aria-labelledby="gallery-hero-heading">
      <OptimizedBackgroundImage
        src={heroImage}
        lazy={false}
        className="absolute inset-0"
        backgroundSize="cover"
        backgroundPosition="center"
      >
        <div className="absolute inset-0" />
      </OptimizedBackgroundImage>
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background z-10" />

      <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4 z-20">
        <motion.h1
          id="gallery-hero-heading"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-serif text-3xl md:text-5xl font-bold text-center mb-3"
        >
          Customer Showcase
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-lg md:text-xl text-white/90 text-center max-w-2xl"
        >
          Discover the living worlds our customers have created
        </motion.p>
      </div>
    </section>
  );
}

// Terrarium of the Month Section
function TerrariumOfTheMonth({ entry }: { entry: ShowcaseEntry }) {
  const [, setLocation] = useLocation();

  return (
    <section className="py-12 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5" aria-labelledby="totm-heading">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Trophy className="w-6 h-6 text-yellow-500" aria-hidden="true" />
          <h2 id="totm-heading" className="font-serif text-2xl md:text-3xl font-bold text-center">
            Terrarium of the Month
          </h2>
          <Trophy className="w-6 h-6 text-yellow-500" aria-hidden="true" />
        </div>

        <Card className="overflow-hidden max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="relative aspect-square md:aspect-auto">
              <OptimizedImage
                src={entry.photoUrl}
                alt={`${entry.customerName}'s terrarium`}
                fill
                objectFit="cover"
                className="absolute inset-0"
                priority
              />
              <div className="absolute top-4 left-4 flex gap-2">
                <Badge className="bg-yellow-500 text-yellow-950">
                  <Award className="w-3 h-3 mr-1" />
                  Winner
                </Badge>
              </div>
            </div>

            <CardContent className="p-6 md:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                <MapPin className="w-4 h-4" />
                <span>{entry.customerLocation}</span>
              </div>

              <h3 className="font-serif text-xl md:text-2xl font-semibold mb-2">
                {entry.customerName}
              </h3>

              <p className="text-muted-foreground mb-4 line-clamp-4">
                "{entry.story}"
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {entry.productsUsed.map((product, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {product}
                  </Badge>
                ))}
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <ThumbsUp className="w-4 h-4" />
                  <span className="font-medium">{entry.votes} votes</span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLocation("/checkout")}
                  className="gap-2"
                >
                  <ShoppingBag className="w-4 h-4" />
                  Get the Look
                </Button>
              </div>
            </CardContent>
          </div>
        </Card>
      </div>
    </section>
  );
}

// Filter Bar Component
function FilterBar({
  filters,
  onFilterChange,
}: {
  filters: GalleryFilters;
  onFilterChange: (filters: Partial<GalleryFilters>) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const terrariumTypes = [
    { value: "all", label: "All Types", icon: Leaf },
    { value: "tropical", label: "Tropical", icon: Sparkles },
    { value: "desert", label: "Desert", icon: Sun },
    { value: "moss", label: "Moss", icon: TreePine },
    { value: "forest", label: "Forest", icon: TreePine },
    { value: "custom", label: "Custom", icon: Star },
  ];

  const sizes = [
    { value: "all", label: "All Sizes" },
    { value: "small", label: "Small" },
    { value: "medium", label: "Medium" },
    { value: "large", label: "Large" },
    { value: "extra-large", label: "Extra Large" },
  ];

  return (
    <div className="sticky top-16 z-40 bg-background/95 backdrop-blur-md border-b py-4">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Desktop Filters */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filter:</span>
            </div>

            <Select
              value={filters.terrariumType}
              onValueChange={(value) => onFilterChange({ terrariumType: value })}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Terrarium Type" />
              </SelectTrigger>
              <SelectContent>
                {terrariumTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.size}
              onValueChange={(value) => onFilterChange({ size: value })}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Size" />
              </SelectTrigger>
              <SelectContent>
                {sizes.map((size) => (
                  <SelectItem key={size.value} value={size.value}>
                    {size.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sort Options */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:inline">Sort:</span>
            <Tabs
              value={filters.sortBy}
              onValueChange={(value) => onFilterChange({ sortBy: value as GalleryFilters["sortBy"] })}
            >
              <TabsList className="h-9">
                <TabsTrigger value="featured" className="text-xs px-3">
                  <Star className="w-3 h-3 mr-1" />
                  Featured
                </TabsTrigger>
                <TabsTrigger value="recent" className="text-xs px-3">
                  <Clock className="w-3 h-3 mr-1" />
                  Recent
                </TabsTrigger>
                <TabsTrigger value="popular" className="text-xs px-3">
                  <Heart className="w-3 h-3 mr-1" />
                  Popular
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Mobile Filter Button */}
          <Button
            variant="outline"
            size="sm"
            className="md:hidden"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </div>

        {/* Mobile Filter Panel */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden"
            >
              <div className="pt-4 grid grid-cols-2 gap-3">
                <Select
                  value={filters.terrariumType}
                  onValueChange={(value) => onFilterChange({ terrariumType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {terrariumTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.size}
                  onValueChange={(value) => onFilterChange({ size: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Size" />
                  </SelectTrigger>
                  <SelectContent>
                    {sizes.map((size) => (
                      <SelectItem key={size.value} value={size.value}>
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Showcase Card Component
function ShowcaseCard({
  entry,
  onVote,
  onShare,
  onGetLook,
}: {
  entry: ShowcaseEntry;
  onVote: (id: string) => void;
  onShare: (entry: ShowcaseEntry) => void;
  onGetLook: (entry: ShowcaseEntry) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasVoted, setHasVoted] = useState(entry.hasVoted || false);
  const [voteCount, setVoteCount] = useState(entry.votes);

  const handleVote = () => {
    if (!hasVoted) {
      setHasVoted(true);
      setVoteCount((prev) => prev + 1);
      onVote(entry.id);
    }
  };

  const typeIcons = {
    tropical: Sparkles,
    desert: Sun,
    moss: TreePine,
    forest: TreePine,
    custom: Star,
  };

  const TypeIcon = typeIcons[entry.terrariumType] || Leaf;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="group"
    >
      <Card className="overflow-hidden h-full flex flex-col transition-shadow hover:shadow-lg">
        {/* Image */}
        <div className="relative aspect-square overflow-hidden">
          <OptimizedImage
            src={entry.photoUrl}
            alt={`${entry.customerName}'s ${entry.terrariumType} terrarium`}
            fill
            objectFit="cover"
            className="transition-transform duration-500 group-hover:scale-105"
            rootMargin="300px"
          />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {entry.isFeatured && (
              <Badge className="bg-primary text-primary-foreground">
                <Star className="w-3 h-3 mr-1" />
                Featured
              </Badge>
            )}
            <Badge variant="secondary" className="backdrop-blur-sm">
              <TypeIcon className="w-3 h-3 mr-1" />
              {entry.terrariumType.charAt(0).toUpperCase() + entry.terrariumType.slice(1)}
            </Badge>
          </div>

          {/* Actions Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="absolute bottom-3 right-3 flex gap-2">
              <Button
                size="icon"
                variant="secondary"
                className="h-8 w-8 backdrop-blur-sm"
                onClick={() => onShare(entry)}
                aria-label="Share this terrarium"
              >
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <CardContent className="p-4 flex-1 flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-semibold">{entry.customerName}</h3>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {entry.customerLocation}
              </p>
            </div>
            <Badge variant="outline" className="text-xs">
              {entry.terrariumSize}
            </Badge>
          </div>

          <p className={cn(
            "text-sm text-muted-foreground mb-3 flex-1",
            isExpanded ? "" : "line-clamp-3"
          )}>
            "{entry.story}"
          </p>

          {entry.story.length > 120 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-primary hover:underline mb-3 self-start"
            >
              {isExpanded ? "Show less" : "Read more"}
            </button>
          )}

          {/* Products Used */}
          <div className="flex flex-wrap gap-1 mb-3">
            {entry.productsUsed.slice(0, 2).map((product, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {product}
              </Badge>
            ))}
            {entry.productsUsed.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{entry.productsUsed.length - 2}
              </Badge>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t">
            <Button
              variant={hasVoted ? "secondary" : "ghost"}
              size="sm"
              onClick={handleVote}
              disabled={hasVoted}
              className={cn("gap-2", hasVoted && "text-primary")}
            >
              {hasVoted ? (
                <Check className="w-4 h-4" />
              ) : (
                <ThumbsUp className="w-4 h-4" />
              )}
              <span>{voteCount}</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onGetLook(entry)}
              className="gap-2"
            >
              <ShoppingBag className="w-4 h-4" />
              Get the Look
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Masonry Gallery Grid
function MasonryGallery({
  entries,
  onVote,
  onShare,
  onGetLook,
}: {
  entries: ShowcaseEntry[];
  onVote: (id: string) => void;
  onShare: (entry: ShowcaseEntry) => void;
  onGetLook: (entry: ShowcaseEntry) => void;
}) {
  if (entries.length === 0) {
    return (
      <div className="text-center py-16">
        <Camera className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
        <h3 className="font-serif text-xl font-semibold mb-2">No submissions yet</h3>
        <p className="text-muted-foreground mb-6">
          Be the first to share your terrarium creation!
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <AnimatePresence mode="popLayout">
        {entries.map((entry) => (
          <ShowcaseCard
            key={entry.id}
            entry={entry}
            onVote={onVote}
            onShare={onShare}
            onGetLook={onGetLook}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

// Share Modal
function ShareModal({
  entry,
  isOpen,
  onClose,
}: {
  entry: ShowcaseEntry | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();

  if (!entry) return null;

  const shareUrl = `${window.location.origin}/gallery?highlight=${entry.id}`;
  const shareText = `Check out this amazing ${entry.terrariumType} terrarium from Life Itself Studio!`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link copied!",
        description: "The link has been copied to your clipboard.",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const socialLinks = [
    {
      name: "Instagram",
      icon: Instagram,
      url: `https://instagram.com`,
      color: "bg-gradient-to-br from-purple-500 to-pink-500",
    },
    {
      name: "Twitter",
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      color: "bg-sky-500",
    },
    {
      name: "Facebook",
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      color: "bg-blue-600",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share this Terrarium</DialogTitle>
          <DialogDescription>
            Share {entry.customerName}'s beautiful {entry.terrariumType} terrarium with your friends!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preview */}
          <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
              <OptimizedImage
                src={entry.photoUrl}
                alt="Terrarium preview"
                fill
                objectFit="cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{entry.customerName}'s Terrarium</p>
              <p className="text-sm text-muted-foreground truncate">
                {entry.terrariumType.charAt(0).toUpperCase() + entry.terrariumType.slice(1)} - {entry.terrariumSize}
              </p>
            </div>
          </div>

          {/* Social Buttons */}
          <div className="grid grid-cols-3 gap-3">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex flex-col items-center justify-center gap-2 p-3 rounded-lg text-white transition-transform hover:scale-105",
                  social.color
                )}
              >
                <social.icon className="w-5 h-5" />
                <span className="text-xs font-medium">{social.name}</span>
              </a>
            ))}
          </div>

          {/* Copy Link */}
          <div className="flex gap-2">
            <Input
              value={shareUrl}
              readOnly
              className="flex-1 text-sm"
            />
            <Button variant="outline" onClick={handleCopyLink}>
              <LinkIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Submission Form Modal
function SubmissionForm({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<void>;
}) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    firstName: "",
    location: "",
    story: "",
    terrariumType: "",
    terrariumSize: "",
    productsUsed: [] as string[],
    photo: null as File | null,
    email: "",
    agreeToTerms: false,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid file type",
          description: "Please upload an image file (JPG, PNG, etc.)",
          variant: "destructive",
        });
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 10MB",
          variant: "destructive",
        });
        return;
      }
      setFormData({ ...formData, photo: file });
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "productsUsed") {
          data.append(key, JSON.stringify(value));
        } else if (value !== null) {
          data.append(key, value as string | Blob);
        }
      });
      await onSubmit(data);
      toast({
        title: "Submission received!",
        description: "Your terrarium will be reviewed and added to the gallery soon.",
      });
      onClose();
    } catch (error) {
      toast({
        title: "Submission failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStep1Valid = formData.photo && formData.firstName && formData.location;
  const isStep2Valid = formData.story && formData.terrariumType && formData.terrariumSize;
  const isStep3Valid = formData.email && formData.agreeToTerms;

  const productOptions = [
    "Forest Oasis Terrarium",
    "DesertScape Terrarium",
    "Tropical Paradise Terrarium",
    "Deluxe Custom Terrarium",
    "Premium Moss Blend",
    "Miniature Ferns",
    "Succulent Collection",
    "Exotic Ferns",
    "Decorative Stones",
    "Workshop Experience",
    "Custom Design Consultation",
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" />
            Share Your Terrarium
          </DialogTitle>
          <DialogDescription>
            Step {step} of 3 - {step === 1 ? "Upload Photo" : step === 2 ? "Tell Your Story" : "Confirm & Submit"}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="flex gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={cn(
                "h-1 flex-1 rounded-full transition-colors",
                s <= step ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>

        <div className="space-y-4">
          {/* Step 1: Photo & Basic Info */}
          {step === 1 && (
            <>
              <div>
                <Label>Photo of Your Terrarium *</Label>
                <div className="mt-2">
                  {previewUrl ? (
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <Button
                        size="icon"
                        variant="secondary"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          setPreviewUrl(null);
                          setFormData({ ...formData, photo: null });
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center aspect-video rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 transition-colors cursor-pointer bg-muted/50">
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-muted-foreground/60 mt-1">
                        JPG, PNG up to 10MB
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    placeholder="Sarah"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    placeholder="Denver, CO"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}

          {/* Step 2: Story & Details */}
          {step === 2 && (
            <>
              <div>
                <Label htmlFor="story">Your Terrarium Story *</Label>
                <Textarea
                  id="story"
                  placeholder="Tell us about your terrarium! How did you decide on this design? What do you love about it?"
                  value={formData.story}
                  onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                  rows={4}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.story.length}/500 characters
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Terrarium Type *</Label>
                  <Select
                    value={formData.terrariumType}
                    onValueChange={(value) => setFormData({ ...formData, terrariumType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tropical">Tropical</SelectItem>
                      <SelectItem value="desert">Desert</SelectItem>
                      <SelectItem value="moss">Moss</SelectItem>
                      <SelectItem value="forest">Forest</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Terrarium Size *</Label>
                  <Select
                    value={formData.terrariumSize}
                    onValueChange={(value) => setFormData({ ...formData, terrariumSize: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="large">Large</SelectItem>
                      <SelectItem value="extra-large">Extra Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Products Used (optional)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select the products included in your terrarium
                </p>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                  {productOptions.map((product) => (
                    <Badge
                      key={product}
                      variant={formData.productsUsed.includes(product) ? "default" : "outline"}
                      className="cursor-pointer transition-colors"
                      onClick={() => {
                        const updated = formData.productsUsed.includes(product)
                          ? formData.productsUsed.filter((p) => p !== product)
                          : [...formData.productsUsed, product];
                        setFormData({ ...formData, productsUsed: updated });
                      }}
                    >
                      {formData.productsUsed.includes(product) && (
                        <Check className="w-3 h-3 mr-1" />
                      )}
                      {product}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  We'll notify you when your submission is approved
                </p>
              </div>

              <Card className="p-4 bg-muted/50">
                <h4 className="font-medium mb-2">Submission Summary</h4>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p><strong>Name:</strong> {formData.firstName} from {formData.location}</p>
                  <p><strong>Type:</strong> {formData.terrariumType} ({formData.terrariumSize})</p>
                  <p><strong>Products:</strong> {formData.productsUsed.length > 0 ? formData.productsUsed.join(", ") : "Not specified"}</p>
                </div>
              </Card>

              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <input
                  type="checkbox"
                  id="terms"
                  checked={formData.agreeToTerms}
                  onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
                  className="mt-1"
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground">
                  I agree to let Life Itself Studio feature my photo and story in the customer showcase gallery.
                  My submission will be reviewed before being published.
                </label>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex gap-2 pt-4">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          )}
          <div className="flex-1" />
          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 ? !isStep1Valid : !isStep2Valid}
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!isStep3Valid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Submit
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Get the Look Modal
function GetTheLookModal({
  entry,
  isOpen,
  onClose,
}: {
  entry: ShowcaseEntry | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [, setLocation] = useLocation();

  if (!entry) return null;

  const products = [
    { name: "Forest Oasis Terrarium", price: "$499", image: mossTerrarium },
    { name: "DesertScape Terrarium", price: "$499", image: desertTerrarium },
    { name: "Tropical Paradise Terrarium", price: "$649", image: tropicalTerrarium },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-primary" />
            Get the Look
          </DialogTitle>
          <DialogDescription>
            Create a similar terrarium to {entry.customerName}'s stunning {entry.terrariumType} design
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Original Image */}
          <div className="relative aspect-video rounded-lg overflow-hidden">
            <OptimizedImage
              src={entry.photoUrl}
              alt={`${entry.customerName}'s terrarium`}
              fill
              objectFit="cover"
            />
          </div>

          {/* Products Used */}
          <div>
            <h4 className="font-medium mb-3">Products in this look:</h4>
            <div className="space-y-3">
              {entry.productsUsed.map((productName, idx) => {
                const product = products.find(p => p.name === productName) || {
                  name: productName,
                  price: "Custom",
                  image: mossTerrarium,
                };
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-2 rounded-lg border hover:border-primary transition-colors cursor-pointer"
                    onClick={() => setLocation("/checkout")}
                  >
                    <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                      <OptimizedImage
                        src={product.image}
                        alt={product.name}
                        fill
                        objectFit="cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{product.name}</p>
                      <p className="text-sm text-primary">{product.price}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => setLocation("/checkout")} className="gap-2">
            <Leaf className="w-4 h-4" />
            Design Your Own
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Voting CTA Section
function VotingCTA() {
  return (
    <section className="py-12 bg-gradient-to-r from-yellow-500/10 via-yellow-500/20 to-yellow-500/10">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Trophy className="w-6 h-6 text-yellow-500" />
          <h2 className="font-serif text-2xl font-bold">Vote for Terrarium of the Month</h2>
        </div>
        <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
          Help choose the next featured terrarium! Browse the gallery and vote for your favorite
          creations. The winner gets a featured spotlight and a $50 Life Itself Studio gift card!
        </p>
        <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>Voting ends January 31st</span>
          </div>
          <div className="flex items-center gap-2">
            <ThumbsUp className="w-4 h-4" />
            <span>One vote per person</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// Main Gallery Page Component
export default function Gallery() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [filters, setFilters] = useState<GalleryFilters>({
    terrariumType: "all",
    size: "all",
    sortBy: "featured",
  });
  const [shareEntry, setShareEntry] = useState<ShowcaseEntry | null>(null);
  const [getLookEntry, setGetLookEntry] = useState<ShowcaseEntry | null>(null);
  const [isSubmissionOpen, setIsSubmissionOpen] = useState(false);

  // For now, use placeholder data
  // In production, this would fetch from the API
  const entries = useMemo(() => {
    let filtered = [...PLACEHOLDER_ENTRIES];

    // Apply type filter
    if (filters.terrariumType !== "all") {
      filtered = filtered.filter(e => e.terrariumType === filters.terrariumType);
    }

    // Apply size filter
    if (filters.size !== "all") {
      filtered = filtered.filter(e => e.terrariumSize === filters.size);
    }

    // Apply sorting
    switch (filters.sortBy) {
      case "popular":
        filtered.sort((a, b) => b.votes - a.votes);
        break;
      case "recent":
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case "featured":
      default:
        filtered.sort((a, b) => {
          if (a.isFeatured && !b.isFeatured) return -1;
          if (!a.isFeatured && b.isFeatured) return 1;
          return b.votes - a.votes;
        });
    }

    return filtered;
  }, [filters]);

  // Handlers
  const handleFilterChange = useCallback((newFilters: Partial<GalleryFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleVote = useCallback((id: string) => {
    toast({
      title: "Vote recorded!",
      description: "Thanks for voting for this terrarium!",
    });
  }, [toast]);

  const handleShare = useCallback((entry: ShowcaseEntry) => {
    setShareEntry(entry);
  }, []);

  const handleGetLook = useCallback((entry: ShowcaseEntry) => {
    setGetLookEntry(entry);
  }, []);

  const handleSubmit = useCallback(async (data: FormData) => {
    // In production, this would POST to the API
    console.log("Submission data:", Object.fromEntries(data.entries()));
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Skip Link */}
      <a
        href="#gallery-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md"
      >
        Skip to gallery
      </a>

      <GalleryHeader />

      <main id="gallery-content" tabIndex={-1}>
        <GalleryHero />

        {/* Terrarium of the Month */}
        <TerrariumOfTheMonth entry={TERRARIUM_OF_THE_MONTH} />

        {/* Voting CTA */}
        <VotingCTA />

        {/* Filter Bar */}
        <FilterBar filters={filters} onFilterChange={handleFilterChange} />

        {/* Gallery Grid */}
        <section className="py-12" aria-label="Customer showcase gallery">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-serif text-2xl font-bold">
                  {filters.sortBy === "featured" ? "Featured Creations" :
                   filters.sortBy === "popular" ? "Most Loved" : "Latest Submissions"}
                </h2>
                <p className="text-muted-foreground">
                  {entries.length} terrarium{entries.length !== 1 ? "s" : ""} to explore
                </p>
              </div>

              <Button onClick={() => setIsSubmissionOpen(true)} className="gap-2">
                <Camera className="w-4 h-4" />
                <span className="hidden sm:inline">Share Yours</span>
              </Button>
            </div>

            <MasonryGallery
              entries={entries}
              onVote={handleVote}
              onShare={handleShare}
              onGetLook={handleGetLook}
            />

            {/* Load More */}
            {entries.length >= 6 && (
              <div className="mt-12 text-center">
                <Button variant="outline" size="lg">
                  Load More
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="font-serif text-3xl font-bold mb-4">
              Ready to Create Your Own?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              Join our community of terrarium enthusiasts and craft your own living masterpiece.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" onClick={() => setLocation("/checkout")} className="gap-2">
                <Leaf className="w-5 h-5" />
                Design Your Terrarium
              </Button>
              <Button size="lg" variant="outline" onClick={() => setLocation("/workshop")} className="gap-2">
                <Calendar className="w-5 h-5" />
                Book a Workshop
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Life Itself Studio. All rights reserved.</p>
        </div>
      </footer>

      {/* Modals */}
      <ShareModal
        entry={shareEntry}
        isOpen={!!shareEntry}
        onClose={() => setShareEntry(null)}
      />

      <GetTheLookModal
        entry={getLookEntry}
        isOpen={!!getLookEntry}
        onClose={() => setGetLookEntry(null)}
      />

      <SubmissionForm
        isOpen={isSubmissionOpen}
        onClose={() => setIsSubmissionOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
